import { useIntl } from 'react-intl';
import { Checkbox, Input, RadioButtonsGroup, Selector, SelectorOptionPropsInterface } from 'sonata-react-components';

import { DeliveryStrategySectionStyled } from './DeliveryStrategySection.styles.ts';

import type { FrequencyCapping } from 'sonata-static-storage/lib/schemas/multiFlight/frequencyCapping.schema';

type Props = {
  errors: Record<string, any>;
  isDooh?: boolean;
  setUserFrequencyCapping: (FrequencyCapping: FrequencyCapping) => void;
  userFrequencyCapping: FrequencyCapping;
};

export default function DeliveryStrategySection({
  errors,
  isDooh,
  setUserFrequencyCapping,
  userFrequencyCapping
}: Props) {
  const { formatMessage } = useIntl();

  const {
    pacing,
    userFrequencyCappingEnabled,
    userFrequencyClickCappingEnabled,
    userMaxClicks,
    userMaxClicksHours,
    userMaxImpressions,
    userMaxImpressionsHours
  } = userFrequencyCapping;

  const radioButtonsGroup = [
    {
      caption: formatMessage({
        defaultMessage: 'Distribute ads evenly over time.',
        id: 'deliveryStrategySection.options.balancedDescription'
      }),
      id: 'balanced',
      text: formatMessage({
        defaultMessage: 'Balanced',
        id: 'deliveryStrategySection.options.balanced'
      })
    },
    {
      caption: formatMessage({
        defaultMessage: 'Show Ads as fast as possible.',
        id: 'deliveryStrategySection.options.boostDescription'
      }),
      id: 'boost',
      text: formatMessage({
        defaultMessage: 'Boost',
        id: 'deliveryStrategySection.options.boost'
      })
    }
  ];

  const calendarOptions = [
    {
      id: '1',
      label: formatMessage({
        defaultMessage: 'Hour',
        id: 'deliveryStrategySection.calendarOptions.hour'
      })
    },
    {
      id: '24',
      label: formatMessage({
        defaultMessage: 'Day',
        id: 'deliveryStrategySection.calendarOptions.day'
      })
    },
    {
      id: '168',
      label: formatMessage({
        defaultMessage: 'Week',
        id: 'deliveryStrategySection.calendarOptions.week'
      })
    },
    {
      id: '744',
      label: formatMessage({
        defaultMessage: 'Month',
        id: 'deliveryStrategySection.calendarOptions.month'
      })
    },
    {
      id: '8760',
      label: formatMessage({
        defaultMessage: 'Year',
        id: 'deliveryStrategySection.calendarOptions.year'
      })
    }
  ];

  const handleDeliveryStrategyChange = (id: string) => {
    setUserFrequencyCapping({
      ...userFrequencyCapping,
      pacing: id === 'balanced'
    });
  };

  const handleAdShownChange = (val: boolean) => {
    setUserFrequencyCapping({
      ...userFrequencyCapping,
      userFrequencyCappingEnabled: val
    });
  };

  const handleAdClickedChange = (val: boolean) => {
    setUserFrequencyCapping({
      ...userFrequencyCapping,
      userFrequencyClickCappingEnabled: val
    });
  };

  const handleUserMaxImpressionsChange = (val: string) => {
    setUserFrequencyCapping({
      ...userFrequencyCapping,
      userMaxImpressions: parseInt(val)
    });
  };

  const handleUserMaxImpressionsHoursChange = (val: SelectorOptionPropsInterface) => {
    setUserFrequencyCapping({
      ...userFrequencyCapping,
      userMaxImpressionsHours: parseInt(val.value!)
    });
  };

  const handleUserMaxClicksChange = (val: string) => {
    setUserFrequencyCapping({
      ...userFrequencyCapping,
      userMaxClicks: parseInt(val)
    });
  };

  const handleUserMaxClicksHoursChange = (val: SelectorOptionPropsInterface) => {
    setUserFrequencyCapping({
      ...userFrequencyCapping,
      userMaxClicksHours: parseInt(val.value!)
    });
  };

  const hasErrorUserFrequencyCappingEnabled =
    errors?.userFrequencyCapping?.userMaxImpressions || errors?.userFrequencyCapping?.userMaxImpressionsHours;
  const hasErrorUserFrequencyClickCappingEnabled =
    errors?.userFrequencyCapping?.userMaxClicks || errors?.userFrequencyCapping?.userMaxClicksHours;

  return (
    <DeliveryStrategySectionStyled className="deliveryStrategySection">
      <div className="deliveryStrategySection__title">
        {formatMessage({
          defaultMessage: 'Delivery Strategy',
          id: 'deliveryStrategySection.title'
        })}
      </div>
      <div className="deliveryStrategySection__description">
        {formatMessage({
          defaultMessage: 'Set how quickly your ads are delivered',
          id: 'deliveryStrategySection.description'
        })}
      </div>
      <div className="deliveryStrategySection__radios">
        <RadioButtonsGroup
          id="deliveryStrategySectionRadios"
          items={radioButtonsGroup}
          onChange={handleDeliveryStrategyChange}
          value={pacing ? 'balanced' : 'boost'}
        />
      </div>
      {!isDooh && (
        <>
          <div className="deliveryStrategySection__check">
            <Checkbox
              active={userFrequencyCappingEnabled}
              classes="deliveryStrategySection__checkBox"
              colorSchema="secondary"
              id="deliveryStrategyShown"
              labelText={formatMessage({
                defaultMessage: 'Limit the Number of Times an Ad is Shown',
                id: 'deliveryStrategySection.switch.adShown'
              })}
              onClickAction={(_: any, checked: boolean) => handleAdShownChange(checked)}
              type="switch"
            />
            {userFrequencyCappingEnabled && (
              <>
                {hasErrorUserFrequencyCappingEnabled && (
                  <div className="deliveryStrategySection__error">
                    <i className="fa-regular fa-circle-x fa-xs" />
                    {formatMessage({
                      defaultMessage: 'Please check required fields',
                      id: 'ioSettings.errors.userFrequencyCapping'
                    })}
                  </div>
                )}
                <div className="deliveryStrategySection__checkContainer">
                  {formatMessage({
                    defaultMessage: 'No more than',
                    id: 'deliveryStrategySection.noMore'
                  })}
                  <Input
                    className="deliveryStrategySection__checkInput"
                    id="deliveryStrategyShownInput"
                    inputStatus={errors?.userFrequencyCapping?.userMaxImpressions ? 'wrong' : 'default'}
                    name="deliveryStrategyShownInput"
                    onChange={(val: any) => handleUserMaxImpressionsChange(val as string)}
                    shape="squareUI"
                    type="number"
                    value={userMaxImpressions}
                  />
                  {formatMessage({
                    defaultMessage: 'per',
                    id: 'deliveryStrategySection.per'
                  })}
                  <Selector
                    className="deliveryStrategySection__checkSelector"
                    onChange={value => handleUserMaxImpressionsHoursChange(value as SelectorOptionPropsInterface)}
                    options={calendarOptions}
                    selectorStatus={errors?.userFrequencyCapping?.userMaxImpressionsHours ? 'wrong' : 'default'}
                    type="simple"
                    value={
                      userMaxImpressionsHours
                        ? [calendarOptions.find(val => val.id === userMaxImpressionsHours.toString())!]
                        : []
                    }
                  />
                </div>
              </>
            )}
          </div>
          <div className="deliveryStrategySection__check">
            <Checkbox
              active={userFrequencyClickCappingEnabled}
              classes="deliveryStrategySection__checkBox"
              colorSchema="secondary"
              id="deliveryStrategyClicked"
              labelText={formatMessage({
                defaultMessage: 'Limit the Number of Times an Ad is Clicked',
                id: 'deliveryStrategySection.switch.adClicked'
              })}
              onClickAction={(_: any, checked: boolean) => handleAdClickedChange(checked)}
              type="switch"
            />
            {userFrequencyClickCappingEnabled && (
              <>
                {hasErrorUserFrequencyClickCappingEnabled && (
                  <div className="deliveryStrategySection__error">
                    <i className="fa-regular fa-circle-x fa-xs" />
                    {formatMessage({
                      defaultMessage: 'Please check required fields',
                      id: 'ioSettings.errors.userFrequencyCapping'
                    })}
                  </div>
                )}
                <div className="deliveryStrategySection__checkContainer">
                  {formatMessage({
                    defaultMessage: 'No more than',
                    id: 'deliveryStrategySection.noMore'
                  })}
                  <Input
                    className="deliveryStrategySection__checkInput"
                    id="deliveryStrategyClickedInput"
                    inputStatus={errors?.userFrequencyCapping?.userMaxClicks ? 'wrong' : 'default'}
                    name="deliveryStrategyClickedInput"
                    onChange={(val: any) => handleUserMaxClicksChange(val as string)}
                    shape="squareUI"
                    type="number"
                    value={userMaxClicks}
                  />
                  {formatMessage({
                    defaultMessage: 'per',
                    id: 'deliveryStrategySection.per'
                  })}
                  <Selector
                    className="deliveryStrategySection__checkSelector"
                    onChange={val => handleUserMaxClicksHoursChange(val as SelectorOptionPropsInterface)}
                    options={calendarOptions}
                    selectorStatus={errors?.userFrequencyCapping?.userMaxClicksHours ? 'wrong' : 'default'}
                    type="simple"
                    value={
                      userMaxClicksHours ? [calendarOptions.find(val => val.id === userMaxClicksHours.toString())!] : []
                    }
                  />
                </div>
              </>
            )}
          </div>
        </>
      )}
    </DeliveryStrategySectionStyled>
  );
}
